import { useEffect, useRef, useState } from "react";
import BarcodeScannerModal from "./common/BarcodeScannerModal";
import { findBottles, type BottleSearchMode, type LocatedBottle } from "../lib/bottleLocator";
import { sortedShelfLocations } from "../lib/shelfLocations";
import "./FindBottleModal.css";

interface Props {
  onClose: () => void;
  /** Open with the barcode scanner already running (the Hub's scan button). */
  startScanning?: boolean;
  /** Label for the footer close button; Station returns to a drink, the Hub does not. */
  backLabel?: string;
}

export default function FindBottleModal({ onClose, startScanning = false, backLabel = "Back to drink" }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const generation = useRef(0);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<BottleSearchMode>("name");
  const [bottles, setBottles] = useState<LocatedBottle[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(false);
  const [scanning, setScanning] = useState(startScanning);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const scroll = { left: window.scrollX, top: window.scrollY };
    const overflow = document.body.style.overflow;
    const overflowAnchor = document.documentElement.style.overflowAnchor;
    document.documentElement.style.overflowAnchor = "none";
    const dialog = dialogRef.current;
    dialog?.showModal();
    document.body.style.overflow = "hidden";
    inputRef.current?.focus({ preventScroll: true });
    return () => {
      generation.current++;
      dialog?.close();
      document.body.style.overflow = overflow;
      previousFocus?.focus({ preventScroll: true });
      window.scrollTo({ ...scroll, behavior: "instant" });
      document.documentElement.style.overflowAnchor = overflowAnchor;
    };
  }, []);

  useEffect(() => {
    const updateOnline = () => {
      generation.current++;
      setOnline(navigator.onLine);
      setBottles([]);
      setOffset(0);
      setHasMore(false);
      setSearched(false);
    };
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);

  useEffect(() => {
    const current = ++generation.current;
    if (!query.trim() || !online) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    const timer = window.setTimeout(() => {
      void findBottles(query.trim(), mode, offset).then(result => {
        if (current !== generation.current) return;
        setBottles(previous => offset === 0 ? result.bottles : [
          ...previous, ...result.bottles.filter(b => !previous.some(existing => existing.id === b.id)),
        ]);
        setHasMore(result.has_more);
        setSearched(true);
      }).catch(() => {
        if (current === generation.current) setError(true);
      }).finally(() => {
        if (current === generation.current) setLoading(false);
      });
    }, mode === "barcode" || offset > 0 ? 0 : 200);
    return () => {
      window.clearTimeout(timer);
      generation.current++;
    };
  }, [query, mode, offset, attempt, online]);

  function changeSearch(value: string, nextMode: BottleSearchMode = "name") {
    generation.current++;
    setQuery(value);
    setMode(nextMode);
    setOffset(0);
    setBottles([]);
    setHasMore(false);
    setSearched(false);
    setError(false);
    setLoading(Boolean(value.trim()) && online);
    setAttempt(n => n + 1);
  }

  return (
    <dialog ref={dialogRef} className="bottle-finder" aria-labelledby="bottle-finder-title"
      onCancel={event => {
        event.preventDefault();
        if (scanning) { setScanning(false); inputRef.current?.focus(); }
        else closeRef.current();
      }}>
      <div className="bottle-finder-content" ref={node => { if (scanning) node?.setAttribute("inert", ""); else node?.removeAttribute("inert"); }}>
        <header className="bottle-finder-heading">
          <h2 id="bottle-finder-title">Find bottle</h2>
          <p>Locate bottles across all shelves.</p>
        </header>
        <form className="bottle-finder-search" onSubmit={event => { event.preventDefault(); changeSearch(query, mode); }}>
          <label htmlFor="bottle-finder-query">Bottle name or UPC</label>
          <div className="bottle-finder-search-row">
            <input ref={inputRef} id="bottle-finder-query" type="search" value={query} autoComplete="off"
              placeholder="Start typing a bottle name or UPC"
              onChange={event => changeSearch(event.target.value)} />
            <button type="button" className="bottle-finder-scan" aria-label="Scan barcode" title="Scan barcode" onClick={() => setScanning(true)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 7V5a2 2 0 012-2h2" /><path d="M17 3h2a2 2 0 012 2v2" />
                <path d="M21 17v2a2 2 0 01-2 2h-2" /><path d="M7 21H5a2 2 0 01-2-2v-2" />
                <line x1="7" y1="7" x2="7" y2="17" strokeWidth="1.5" />
                <line x1="9.5" y1="7" x2="9.5" y2="17" strokeWidth="2.2" />
                <line x1="11.5" y1="7" x2="11.5" y2="17" strokeWidth="1" />
                <line x1="13.5" y1="7" x2="13.5" y2="17" strokeWidth="2.2" />
                <line x1="15.5" y1="7" x2="15.5" y2="15" strokeWidth="1" />
                <line x1="17" y1="7" x2="17" y2="17" strokeWidth="1.5" />
                <line x1="5" y1="12" x2="19" y2="12" stroke="var(--gold)" strokeWidth="1.5" opacity="0.7" />
              </svg>
            </button>
          </div>
        </form>
        <div className="bottle-finder-results" aria-busy={loading}>
          <div role="status" aria-live="polite">
            {!online ? <p>You’re offline. Connect to look up current shelf locations.</p>
              : error ? <p>Locations couldn’t load. <button type="button" onClick={() => setAttempt(n => n + 1)}>Retry</button></p>
              : loading ? <p>Searching bottles…</p>
              : !query.trim() ? <p>Search by name, enter a UPC, or scan a barcode.</p>
              : searched && bottles.length === 0 ? <p>No matching bottle. Try another name or check the UPC.</p>
              : searched ? <p>{bottles.length}{hasMore ? "+" : ""} matching {bottles.length === 1 && !hasMore ? "bottle" : "bottles"}</p> : null}
          </div>
          {bottles.map(bottle => (
            <article className="bottle-finder-card" key={bottle.id}>
              <h3>{bottle.name}</h3>
              {bottle.distillery && <p>{bottle.distillery}</p>}
              {(mode === "barcode" || /^\d+$/.test(query.trim())) && <button type="button" onClick={() => changeSearch(bottle.name.replace(/\s*-\s*\d+(?:\.\d+)?\s*oz$/i, "").trim())}>Search this name</button>}
              {bottle.matched_sizes.length > 0 && <p>Barcode size: {bottle.matched_sizes.join(", ")}. Locations are recorded for the product; size at each shelf is unverified.</p>}
              {bottle.locations.length === 0 ? <p>Bottle found; no location recorded.</p> : <ul>
                {sortedShelfLocations(bottle.locations).map(location => (
                  <li key={location.code}>
                    <span className={`bottle-finder-status bottle-finder-status-${location.rank}`}>{location.status}</span>
                    <span>{location.area}{location.shelf ? ` → ${location.shelf}` : ""}</span>
                    <strong>{location.code}</strong>
                  </li>
                ))}
              </ul>}
            </article>
          ))}
          {hasMore && !error && <button type="button" disabled={loading} onClick={() => setOffset(bottles.length)}>Load more bottles</button>}
        </div>
        <footer className="bottle-finder-footer">
          <p>Open / closed describes the shelf type, not a verified bottle state or available quantity.</p>
          <button type="button" className="bottle-finder-back" onClick={() => closeRef.current()}>{backLabel}</button>
        </footer>
      </div>
      {scanning && <BarcodeScannerModal isOpen lockOrientation={false}
        onClose={() => { setScanning(false); inputRef.current?.focus({ preventScroll: true }); }}
        onScan={code => { setScanning(false); changeSearch(code, "barcode"); inputRef.current?.focus({ preventScroll: true }); }} />}
    </dialog>
  );
}
